import { layOutOptions, mulberry32, seedFrom } from "./random.js";
/**
 * Sequence completion — the pattern repeats, so what comes next?
 *
 * A strip of shapes with a gap at the end, and the child picks the piece that continues it. The
 * oldest reasoning task there is, and the one that needs no explanation: a repetition is visible
 * without being described, which matters here because nothing on screen may be text (v2 §15).
 *
 * **A token is a shape AND a colour**, packed into one integer so it travels through the existing
 * `number[]` stimulus fields without a protocol change. Varying two attributes rather than one is
 * what separates the easy levels from the real ones: with colour fixed the rule is "shapes take
 * turns", and with both varying the child has to notice that the two cycle together.
 *
 * **Three kinds of pattern, and the kind is what the later levels add.** The first version had one
 * — a fixed unit repeating — which is where a preschool curriculum starts and not where it ends:
 *
 *   - `repeating` (levels 1–5): ABAB, ABCABC. A unit of distinct items, cycling.
 *   - `templated` (6–8): ABB, AABB. A unit with a shape of its own. This is what a preschool
 *     teacher means by "pattern" more often than ABAB is, and the first version could not express
 *     it at all, because it drew units of DISTINCT items on purpose.
 *   - `growing` (9–10): A B AA B AAA B. The first rule that is about change rather than sameness,
 *     and the one that leads towards number.
 */
/**
 * Six icons and four colours: 24 distinct tokens.
 *
 * Six rather than five so a period-3 unit has room to differ across trials without repeating the
 * same three icons every time — with five, two of any three units overlapped and the strips started
 * looking alike.
 */
export const SHAPES = 6;
export const COLORS = 4;
/** shape × colour packed into one integer. Unpacked by the renderer, opaque everywhere else. */
export const packToken = (shape, color) => shape * COLORS + color;
export const tokenShape = (token) => Math.floor(token / COLORS);
export const tokenColor = (token) => token % COLORS;
export const SEQUENCE_MIN_DIFFICULTY = 1;
export const SEQUENCE_MAX_DIFFICULTY = 10;
/**
 * The colour world's own ladder: eight levels, all of them one shape in many colours.
 *
 * Shorter than the shape ladder because it runs out of honest difficulty sooner — with four colours
 * there is no equivalent of a six-shape alphabet, and padding it with longer strips would make it
 * tedious rather than harder.
 */
export const COLOR_SEQUENCE_MAX_DIFFICULTY = 8;
/** Levels for the colour world. Colour does every job that shape does in the other one. */
function colorRecipeFor(difficulty) {
    const base = { colorVaries: true, colorOnly: true };
    switch (difficulty) {
        // AB, the two-colour alternation. The first pattern a four-year-old reads.
        case 1:
            return { ...base, kind: "repeating", template: [0, 1], optionCount: 3 };
        case 2:
            return { ...base, kind: "repeating", template: [0, 1], optionCount: 4 };
        // ABC.
        case 3:
            return { ...base, kind: "repeating", template: [0, 1, 2], optionCount: 4 };
        case 4:
            return { ...base, kind: "repeating", template: [0, 1, 2], optionCount: 4 };
        // ABB — a unit with a shape of its own, told entirely in colour.
        case 5:
            return { ...base, kind: "templated", template: [0, 1, 1], optionCount: 4 };
        case 6:
            return { ...base, kind: "templated", template: [0, 0, 1, 1], optionCount: 4 };
        // ABCD, every colour there is.
        case 7:
            return { ...base, kind: "repeating", template: [0, 1, 2, 3], optionCount: 4 };
        default:
            return { ...base, kind: "growing", template: [], optionCount: 4 };
    }
}
/** Difficulty grows one property at a time: the period, then the second attribute, then the kind. */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { kind: "repeating", template: [0, 1], colorVaries: false, optionCount: 3 };
        case 2:
            return { kind: "repeating", template: [0, 1], colorVaries: true, optionCount: 3 };
        case 3:
            return { kind: "repeating", template: [0, 1, 2], colorVaries: false, optionCount: 4 };
        case 4:
            return { kind: "repeating", template: [0, 1, 2], colorVaries: true, optionCount: 4 };
        case 5:
            return { kind: "repeating", template: [0, 1, 2], colorVaries: true, optionCount: 5 };
        // ABB — the shortest unit with an internal shape.
        case 6:
            return { kind: "templated", template: [0, 1, 1], colorVaries: false, optionCount: 4 };
        // AABB.
        case 7:
            return { kind: "templated", template: [0, 0, 1, 1], colorVaries: false, optionCount: 4 };
        // ABB again, with colour cycling too, so the unit has two properties AND a shape.
        case 8:
            return { kind: "templated", template: [0, 1, 1], colorVaries: true, optionCount: 5 };
        case 9:
            return { kind: "growing", template: [], colorVaries: false, optionCount: 4 };
        default:
            return { kind: "growing", template: [], colorVaries: true, optionCount: 5 };
    }
}
/**
 * Steps a drawn index on by the trial number.
 *
 * **A fair draw from six shapes repeats, and a repeat two trials running looks like the game froze.**
 * The strips themselves were varied enough; what a child noticed was the same two icons coming back.
 * Rotating by the trial index guarantees consecutive trials differ while leaving WHICH shapes appear
 * entirely to the draw — the alternative, re-drawing until it differs, biases the distribution and
 * still cannot promise anything.
 *
 * Distinctness survives: adding a constant modulo the alphabet size is a bijection, so a set of
 * distinct shapes stays distinct.
 */
const rotate = (value, trialIndex, size) => (value + trialIndex) % size;
export function generateSequence(task, formId, trialIndex, difficulty) {
    /**
     * Which ladder, read off the task rather than passed in.
     *
     * The renderer calls one generator for both worlds, so the task it was given is the only thing that
     * knows which one this is. Keyed on the family, not the id, because the two A-13 arms of a game are
     * the same game.
     */
    const colorWorld = task.family === "vs-sequence-color";
    const ceiling = colorWorld ? COLOR_SEQUENCE_MAX_DIFFICULTY : SEQUENCE_MAX_DIFFICULTY;
    if (difficulty < SEQUENCE_MIN_DIFFICULTY || difficulty > ceiling) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const recipe = colorWorld ? colorRecipeFor(difficulty) : recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const built = recipe.kind === "growing"
        ? growing(recipe, rand, trialIndex)
        : cycling(recipe, rand, trialIndex);
    /**
     * The whole row is laid out, not just the answer's slot.
     *
     * Drawing the answer's position was already here and was already right, with a comment saying
     * why — but it was only half the leak. `buildOptions` takes its first distractors from the
     * strip's own token pool, in pool order, so the earliest WRONG card was always a shape already
     * visible in the pattern: measured at a hundred percent of trials, on every level. The answer
     * moved and the rest of the row did not, which is an arrangement a child can learn instead of
     * the rule. `layOutOptions` shuffles the set together, so no slot carries anything.
     */
    const { options, correctIndex } = layOutOptions(built.answer, buildOptions(built.answer, built.pool, recipe.optionCount, rand), rand);
    return { visible: built.visible, options, correctIndex, period: built.period };
}
/**
 * A unit that cycles: both `repeating` and `templated`.
 *
 * The strip shows two full units plus a random remainder, and the remainder is the point: with a
 * whole number of units the answer would always be the first token of the unit, and a child who
 * noticed that would be answering correctly without reading the pattern at all.
 */
function cycling(recipe, rand, trialIndex) {
    const slots = recipe.template;
    const period = slots.length;
    const distinct = new Set(slots).size;
    /**
     * One shape per distinct SLOT, not per position.
     *
     * That is the whole difference between ABC and ABB. The original generator drew a distinct shape
     * per position, which made every unit ABC-shaped and put ABB out of reach — and its comment
     * defended that as avoiding ambiguity, which is true of a random repeat and false of a declared
     * template: `ABBABBA` continues one way.
     */
    /**
     * One shape for every slot in the colour world, a distinct shape per slot in the other.
     *
     * Drawn from the same PRNG either way, so which shape it is still varies by trial — a strip of
     * circles every single time would be as monotonous as the counting level was.
     */
    const shapes = recipe.colorOnly
        ? (() => {
            const one = rotate(Math.floor(rand() * SHAPES), trialIndex, SHAPES);
            return Array.from({ length: distinct }, () => one);
        })()
        : pickDistinct(distinct, SHAPES, rand).map((shape) => rotate(shape, trialIndex, SHAPES));
    const colors = recipe.colorVaries
        ? pickDistinct(distinct, COLORS, rand).map((color) => rotate(color, trialIndex, COLORS))
        : (() => {
            const fixed = rotate(Math.floor(rand() * COLORS), trialIndex, COLORS);
            return shapes.map(() => fixed);
        })();
    const unit = slots.map((slot) => packToken(shapes[slot], colors[slot]));
    const remainder = Math.floor(rand() * period);
    const length = period * 2 + remainder;
    const visible = Array.from({ length }, (_, i) => unit[i % period]);
    const answer = unit[length % period];
    return { visible, answer, pool: unit, period };
}
/**
 * A growing pattern: one item repeated in runs that lengthen, separated by a marker.
 *
 * `A B AA B AAA B AAAA` — and the gap can fall inside a run or on a marker, which is what makes it
 * a pattern question rather than a counting one. The strip is cut at a point drawn from the seed
 * rather than at a run boundary, so the child cannot answer "always the marker next".
 */
function growing(recipe, rand, trialIndex) {
    // In the colour world the run and the marker are the same shape in two colours — the whole pattern
    // has to be readable without shape, or it is the other game.
    const [runShape, markShape] = (recipe.colorOnly
        ? (() => {
            const one = Math.floor(rand() * SHAPES);
            return [one, one];
        })()
        : pickDistinct(2, SHAPES, rand)).map((shape) => rotate(shape, trialIndex, SHAPES));
    const colors = recipe.colorVaries
        ? pickDistinct(2, COLORS, rand).map((color) => rotate(color, trialIndex, COLORS))
        : null;
    const fixed = rotate(Math.floor(rand() * COLORS), trialIndex, COLORS);
    const run = packToken(runShape, colors ? colors[0] : fixed);
    const mark = packToken(markShape, colors ? colors[1] : fixed);
    // Four groups is enough for the rule to be visible and short enough to fit a phone strip:
    // 1+1 + 2+1 + 3+1 + 4 is thirteen tokens.
    const full = [];
    for (let group = 1; group <= 4; group++) {
        for (let i = 0; i < group; i++)
            full.push(run);
        if (group < 4)
            full.push(mark);
    }
    /**
     * Cut somewhere in the last two groups.
     *
     * Earlier than that and the rule is not yet established — two groups do not distinguish
     * "runs grow" from "runs alternate" — so the trial would have more than one defensible answer.
     */
    const earliest = 1 + 1 + 2 + 1 + 1;
    const cut = earliest + Math.floor(rand() * (full.length - earliest));
    const visible = full.slice(0, cut);
    const answer = full[cut];
    return { visible, answer, pool: [run, mark], period: 0 };
}
/**
 * Wrong answers that are wrong for a reason.
 *
 * Drawn first from the pattern's own tokens, because a child who has the rule almost right picks a
 * neighbouring position, and a distractor set of random shapes would let them succeed by
 * elimination without ever finding the pattern.
 */
function buildOptions(answer, pool, optionCount, rand) {
    const out = [];
    const seen = new Set([answer]);
    for (const token of pool) {
        if (out.length >= optionCount - 1)
            break;
        if (!seen.has(token)) {
            seen.add(token);
            out.push(token);
        }
    }
    // Then near misses: the answer with one attribute changed, so the remaining options still
    // require looking at both shape and colour.
    let guard = 0;
    while (out.length < optionCount - 1 && guard++ < 200) {
        const flipShape = rand() < 0.5;
        const token = flipShape
            ? packToken(Math.floor(rand() * SHAPES), tokenColor(answer))
            : packToken(tokenShape(answer), Math.floor(rand() * COLORS));
        if (!seen.has(token)) {
            seen.add(token);
            out.push(token);
        }
    }
    return out;
}
function pickDistinct(count, range, rand) {
    const pool = Array.from({ length: range }, (_, i) => i);
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count);
}
//# sourceMappingURL=sequence.js.map